import type { App } from 'vue'

/**
 * How long a self-closing message stays on screen, in milliseconds.
 */
export const TOAST_LIFE_MS = 5000

/**
 * Severities that stay until the user closes them: the message may still need
 * to be read or acted on, and an error that vanished on its own was the reason
 * for LE-258.
 */
const STICKY_SEVERITIES = new Set(['error', 'warn'])

type ToastMessageLike = { severity?: string; life?: number }
type ToastService = { add: (message: ToastMessageLike) => void }

/**
 * Give every toast a lifetime by severity, once, for the whole app.
 *
 * PrimeVue keeps a message until it is dismissed when it carries no `life`, and
 * no call site set one — so a planning ended with ten or more toasts to click
 * away, successes included (V-01).
 *
 * Applied here rather than at each `toast.add` because there are eighteen call
 * sites and a nineteenth would forget. `ToastService.install` creates a single
 * service object and both assigns it to `$toast` and provides it under
 * `PrimeVueToastSymbol`, so patching that one object covers every `useToast()`
 * in the app as well as the axios interceptor's `toastBus`.
 *
 * A call site that passes its own `life` keeps it. There is deliberately no way
 * to make a success sticky: that is the behaviour being removed.
 *
 * Must run after `app.use(ToastService)`, which is what creates the object.
 */
export function installToastDefaults(app: App) {
  const service = app.config.globalProperties.$toast as ToastService | undefined
  if (!service) return

  const add = service.add.bind(service)
  service.add = (message: ToastMessageLike) => {
    const explicitLife = message?.life != null
    const sticky = STICKY_SEVERITIES.has(message?.severity ?? '')
    if (explicitLife || sticky) return add(message)
    add({ ...message, life: TOAST_LIFE_MS })
  }
}
